











































































































































































































import { Component, Prop, Watch } from 'vue-property-decorator';
import _ from 'lodash';

import { QuestionnaireService } from '@/core/services';
import { QuestionnaireModel, QuestionnaireLookups, FileDownloadModel, CriticalSkillModel } from '@/core/models';
import { SubmitButtonCpt, FirstOpeningQuestionnaireDialogCpt, DialogCpt } from '@/core/components';
import AppVue from '@/AppVue.vue';

import {
  QuestionnaireStep1Cpt,
  QuestionnaireStep2Cpt,
  QuestionnaireStep3Cpt,
  QuestionnaireStep4Cpt,
  QuestionnaireGatewayCpt,
  QuestionnaireGatewayWorkshop1Cpt,
} from '.';
import CheckOutAlertCpt from './CheckOutAlertCpt.vue';
import { Gateway } from '@/core/constants';

@Component({
  components: {
    CheckOutAlertCpt,
    QuestionnaireStep1Cpt,
    QuestionnaireStep2Cpt,
    QuestionnaireStep3Cpt,
    QuestionnaireStep4Cpt,
    SubmitButtonCpt,
    FirstOpeningQuestionnaireDialogCpt,
    QuestionnaireGatewayCpt,
    QuestionnaireGatewayWorkshop1Cpt,
    DialogCpt,
  },
})
export default class QuestionnaireFormCpt extends AppVue {
  @Prop() lookups: QuestionnaireLookups;
  @Prop() model: QuestionnaireModel;
  @Prop({ default: false }) loading: boolean;

  get gateway() {
    return Gateway;
  }

  hasUnsavedChanges = false;
  windowSize = 3000;
  internalLoading = false;
  showOnlyFirstStepDialog = false;
  isSaving = false;

  @Watch('model', { deep: true })
  modelChanged(val: any, oldVal: any) {
    this.hasUnsavedChanges = true;
  }

  created() {
    this.showOnlyFirstStepDialog = false;
    this.$eventHub.$on('QNR_GO_TO_SUBSTEP', async (index: number) => await this.goToSubStep(index));
    this.checkLastStep();
  }

  mounted() {
    window.addEventListener('resize', () => this.calculateWindowWidth());
    this.calculateWindowWidth();
  }

  destroyed() {
    window.removeEventListener('resize', () => this.calculateWindowWidth());
  }

  get isSmallScreen() {
    return this.windowSize <= 1000;
  }

  get isLastStep() {
    return this.model.step >= 6;
  }

  get isFirstStep() {
    return this.model.step <= 0;
  }

  get stepWithSubSteps() {
    return 4;
  }

  get hasSubSteps() {
    return this.model.step === this.stepWithSubSteps;
  }

  get showCheckOutAlert() {
    return (
      this.model.isOpen && (this.isImi || this.isUser || this.isDovetail) && !this.loading && !this.isCheckedOutByMe()
    );
  }

  get skillCount() {
    return this.model.criticalSkills.length;
  }

  get nonEmptySkills() {
    return _.filter(this.model.criticalSkills, (skill: CriticalSkillModel) => !!skill.name);
  }

  get nonEmptySkillCount() {
    return this.nonEmptySkills.length;
  }

  get currentSkillPageText() {
    const readableSubStep = this.hasSubSteps ? this.model.subStep + 1 : 0;
    return `${readableSubStep || '#'}/${this.skillCount || '#'}`;
  }

  get isSubmitButtonDisabled() {
    return this.isLastStep || (this.model.step >= 3 && this.nonEmptySkillCount === 0);
  }

  get isBackButtonEnabled() {
    return !this.isFirstStep;
  }

  get mainWidth() {
    return `${this.windowSize}px`;
  }

  private getStepTitle(step: number) {
    switch (step) {
      case 0:
        return 'Pre-Work for Workshop 1';
      case 1:
        return 'Strategy Clarification';
      case 2:
        return 'Pre-Work for Workshop 2';
      case 3:
        return 'Critical Skills';
      case 4:
        return 'Skills Plans Development';
      case 5:
        return 'Pre-Work for 1 to 1 Session';
      case 6:
        return 'Next Steps';
      default:
        return '';
    }
  }

  checkLastStep() {
    // This is to make the last step be set as complete
    if (this.isLastStep) {
      this.model.step++;
    }
  }

  async downloadPlanOnAPage() {
    try {
      this.model.nextStep = this.model.step;
      await this.updateModel();
      this.internalLoading = true;
      const response: FileDownloadModel = await QuestionnaireService.downloadCriticalSkillsReport(this.model.id);
      await QuestionnaireService.generateCriticalSkillsFile(response);
    } finally {
      this.internalLoading = false;
    }
  }

  async goToStep(nextStep: number) {
    if (nextStep === this.model.step) {
      return;
    }

    if (nextStep >= this.stepWithSubSteps && this.nonEmptySkillCount === 0) {
      return;
    }

    await this.loadStep(nextStep);
  }

  async goToSubStep(nextSubStep: number) {
    if (this.hasSubSteps && nextSubStep === this.model.subStep) {
      return;
    }

    if (this.nonEmptySkillCount === 0) {
      return;
    }

    await this.loadStep(this.stepWithSubSteps, nextSubStep);
  }

  async goNext() {
    if (this.isLastStep) {
      // Finish if goNext() called on the last step
      this.model.nextStep = this.model.step;
      await this.updateModel();
      return;
    }

    if (this.hasSubSteps) {
      if (this.model.subStep === this.getLastNonEmptyCriticalSkillIndex()) {
        // If we're on the last substep, go to the next main step
        await this.loadStep(this.model.step + 1);
        return;
      }
      // Otherwise, go to the next substep
      const nextSubStep = this.getNextNonEmptyCriticalSkillIndex(this.model.subStep);
      await this.goToSubStep(nextSubStep);
      return;
    }
    const nextStep = this.model.step + 1;
    if (nextStep === this.stepWithSubSteps) {
      this.model.subStep = -1;
      await this.goToNextSubStep();
      return;
    }

    await this.loadStep(nextStep);
  }

  async goBack() {
    if (this.isFirstStep) {
      // Finish if goNext() called on the last step
      this.model.nextStep = this.model.step;
      await this.updateModel();
      return;
    }

    if (this.hasSubSteps) {
      if (this.model.subStep === this.getFirstNonEmptyCriticalSkillIndex()) {
        // If we're on the last substep, go to the next main step
        await this.loadStep(this.model.step - 1);
        return;
      }
      // Otherwise, go to the previous substep
      const nextSubStep = this.getPreviousNonEmptyCriticalSkillIndex(this.model.subStep);
      await this.goToSubStep(nextSubStep);
      return;
    }

    const prevStep = this.model.step - 1;
    if (prevStep === this.stepWithSubSteps) {
      this.model.subStep = this.getLastNonEmptyCriticalSkillIndex() + 1;
      await this.goToPreviousSubStep();
      return;
    }

    await this.loadStep(prevStep);
  }

  setHasUnsavedChanges(value: boolean) {
    setTimeout(() => {
      this.hasUnsavedChanges = value;
    });
  }

  private async save() {
    this.model.nextStep = this.model.step;
    await this.updateModel();
  }

  private async updateModel() {
    if (this.isSaving) {
      return false;
    }

    this.isSaving = true;
    this.internalLoading = true;
    try {
      if (this.canEdit()) {
        const response = await QuestionnaireService.putQuestionnaire(this.model);
        _.extend(this.model, response.data);
        this.$notify.success({
          title: 'Details saved',
          message: 'Questionnaire details saved',
          duration: 1750,
          showClose: false,
        });
        this.setHasUnsavedChanges(false);
      }
    } catch (error) {
      // if the response is 422, this means the answers were saved but there are validation errors
      if ((error as any)?.response?.status !== 422) {
        this.$notify({
          title: 'Something went wrong',
          message: 'We could not save your answers. Please try again later',
          type: 'error',
        });
      }
      // this.$eventHub.$emit('REFRESH_QNR_MODEL');
      return false;
    } finally {
      this.internalLoading = false;
      this.isSaving = false;
    }

    return true;
  }

  private async goToNextSubStep() {
    if (this.isLastNonEmptyCriticalSkillIndex(this.model.subStep)) {
      await this.goToStep(this.stepWithSubSteps + 1);
      return;
    }

    const goTo = this.getNextNonEmptyCriticalSkillIndex(this.model.subStep);
    await this.goToSubStep(goTo);
  }

  private async goToPreviousSubStep() {
    if (this.isFirstNonEmptyCriticalSkillIndex(this.model.subStep)) {
      await this.goToStep(this.stepWithSubSteps - 1);
      return;
    }

    const goTo = this.getPreviousNonEmptyCriticalSkillIndex(this.model.subStep);
    await this.goToSubStep(goTo);
  }

  private async loadStep(goToStep: number, goToSubStep: number = 0) {
    this.model.nextStep = goToStep;
    this.model.subStep = goToSubStep;
    await this.updateModel();

    const { step, subStep, moveFocus } = this.determineAllowedStep(goToStep, goToSubStep);
    this.model.step = step;
    this.model.subStep = subStep;

    this.checkLastStep();
    if (moveFocus) {
      this.focusBeginningOfStep();
    }
  }

  private determineAllowedStep(
    targetStep: number,
    targetSubStep: number,
  ): { step: number; subStep: number; moveFocus: boolean } {
    // Check step requirements in order of priority
    if (this.isUser && this.model.isOpen && targetStep > 0) {
      if (this.shouldForceStep0(targetStep)) {
        return { step: 0, subStep: 0, moveFocus: false };
      }

      if (this.shouldForceStep1(targetStep)) {
        return { step: 1, subStep: 0, moveFocus: false };
      }

      if (this.shouldForceStep2(targetStep)) {
        return { step: 2, subStep: 0, moveFocus: false };
      }

      if (this.shouldForceStep3(targetStep)) {
        return { step: 3, subStep: 0, moveFocus: false };
      }

      if (this.shouldForceStep4(targetStep)) {
        return { step: 5, subStep: 0, moveFocus: false };
      }
    }

    // If all checks pass, return the original target step
    return { step: targetStep, subStep: targetSubStep, moveFocus: true };
  }

  private shouldForceStep0(targetStep: number): boolean {
    if (this.model.showOnlyFirstStep) {
      this.showOnlyFirstStepDialog = true;
      return true;
    }

    return false;
  }

  private shouldForceStep1(targetStep: number): boolean {
    const hasNoStrategicCapabilities =
      !this.model.strategicCapability1 &&
      !this.model.strategicCapability2 &&
      !this.model.strategicCapability3 &&
      !this.model.strategicCapability4;

    if (hasNoStrategicCapabilities) {
      this.showNotification(
        'Please fill the strategic capabilities first',
        'You need to fill the strategic capabilities before going to this step',
      );
      return true;
    }

    if (this.model.showOnlyFirstStep) {
      this.showOnlyFirstStepDialog = true;
      return true;
    }

    return false;
  }

  private shouldForceStep2(targetStep: number): boolean {
    if (targetStep > 2 && !this.hasFilledGateway(Gateway.Workshop2)) {
      this.showNotification('Please fill the gateway first', 'You need to fill the gateway before going to this step');
      return true;
    }
    return false;
  }

  private shouldForceStep3(targetStep: number): boolean {
    const hasInvalidCriticalSkills =
      this.model.criticalSkills.length <= 0 || this.model.criticalSkills.some(c => !c.name);

    if (targetStep > 3 && hasInvalidCriticalSkills) {
      this.showNotification(
        'Please fill the critical skills first',
        'You need to fill the critical skills before going to this step',
      );
      return true;
    }
    return false;
  }

  private shouldForceStep4(targetStep: number): boolean {
    if (targetStep > 5 && !this.hasFilledGateway(Gateway.FollowUp)) {
      this.showNotification('Please fill the gateway first', 'You need to fill the gateway before going to this step');
      return true;
    }
    return false;
  }

  private showNotification(title: string, message: string): void {
    this.$notify({
      title,
      message,
      type: 'warning',
    });
  }

  private hasFilledGateway(gateway: Gateway) {
    const gatewaysByWorkshop = this.model.gateways.filter(g => g.gatewayId === gateway);
    return gatewaysByWorkshop.length > 0 && !gatewaysByWorkshop.some(g => !g.percentage);
  }

  private focusBeginningOfStep() {
    this.$nextTick(() => {
      // setTimeout(() => {
      const container = this.$el.querySelector('.qnr-container');
      if (!container) return;

      const firstInput = container.querySelector(
        'button, [href], input, select, textarea, [role="button"], [tabindex]',
      ) as HTMLElement | null;
      if (firstInput) {
        firstInput.focus();
      }
      // }, 500);
    });
  }

  private getFirstNonEmptyCriticalSkillIndex() {
    return _.findIndex(this.model.criticalSkills, (skill: CriticalSkillModel) => !!skill.name);
  }

  private isFirstNonEmptyCriticalSkillIndex(index: number) {
    return index === this.getFirstNonEmptyCriticalSkillIndex();
  }

  private getNextNonEmptyCriticalSkillIndex(currentIndex: number) {
    return _.findIndex(this.model.criticalSkills, (skill, index) => {
      return index > currentIndex && !!skill.name;
    });
  }

  private getLastNonEmptyCriticalSkillIndex() {
    return _.findLastIndex(this.model.criticalSkills, (skill: CriticalSkillModel) => !!skill.name);
  }

  private isLastNonEmptyCriticalSkillIndex(index: number) {
    return index === this.getLastNonEmptyCriticalSkillIndex();
  }

  private getPreviousNonEmptyCriticalSkillIndex(currentIndex: number) {
    return _.findLastIndex(this.model.criticalSkills, (skill, index) => {
      return index < currentIndex && !!skill.name;
    });
  }

  private calculateWindowWidth() {
    setInterval(() => {
      const element = document.querySelector('main');
      this.windowSize = !!element ? element.clientWidth : 3000;
    }, 100);
  }

  private isCheckedOutByMe() {
    return this.model.checkedOutById === this.currentUserId;
  }

  private canEdit() {
    return (this.isImi || this.isUser || this.isDovetail) && this.model.isOpen && this.isCheckedOutByMe();
  }
}
